import {
  FormControlLabel,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import React, { useCallback, useContext } from 'react';
import * as yup from 'yup';
import TextInput from '../../Components/Inputs/TextInput';
import BaseButton from '../../Components/Buttons/BaseButton';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import {
  IDENTIFICATION_TYPE,
  IDENTIFICATION_TYPE_LABELS
} from '../../Enums/users';
import TextAreaInput from '../../Components/Inputs/TextAreaInput';
import BaseDialog from './BaseDialog';
import ControlledCheckboxInput from '../Controlled/ControlledCheckbox';
import { PrivacyPoliciesLink } from '../../Configs/Links';
import { ROUTE_NAMES } from '../../Routes/Routes';
import {
  TERMS,
  TERMS_ROUTES
} from '../../Views/Information/Information/Information';
import CompositeSelectInput from '../Inputs/CompositeSelectInput';
import { UserContext } from '../../Contexts/UserContext';
import {
  DEFAULT_DEPARTMENT,
  DEFAULT_LOCALITY,
  DEPARTMENT_OPTIONS,
  LOCALITY_OPTIONS
} from '../../Enums/locations';
import ControlledSelectInput from '../Controlled/ControlledSelect';
import { ALERT_TYPE } from '../Alerts/alert_enums';
import { AlertsDispatchContext } from '../../Contexts/AlertsContext';
import { CreateConstructorContactAPI } from '../../API/Constructors/ConstructorsAPI';
import _get from 'lodash/get';
import { isPrintableAscii } from '../../Utils/Misc/String';
import { NAMES_REGEX } from '../../Utils/Misc/RegEx';
import { extractErrorMessage } from '../../Utils/Errors/Errors';
import { yupResolver } from '@hookform/resolvers/yup';

const contactInfoSchema = yup
  .object({
    firstName: yup
      .string()
      .trim()
      .matches(NAMES_REGEX, 'No se permiten números ni caracteres especiales')
      .max(80, 'Máximo 80 caracteres')
      .required('Ingresa tu nombre'),
    lastName: yup.mixed().when('identificationType', {
      is: IDENTIFICATION_TYPE.NIT,
      then: yup
        .string()
        .trim()
        .optional(),
      otherwise: yup
        .string()
        .trim()
        .matches(NAMES_REGEX, 'No se permiten números ni caracteres especiales')
        .max(80, 'Máximo 80 caracteres')
        .required('Ingresa tus apellidos')
    }),
    identificationType: yup
      .string()
      .oneOf(IDENTIFICATION_TYPE_LABELS.Full.map(idType => idType.value))
      .required(),
    identification: yup.mixed().when('identificationType', {
      is: IDENTIFICATION_TYPE.PP,
      then: yup
        .string()
        .trim()
        .max(15, 'Máximo 15 carácteres en el pasaporte')
        .matches(/^[0-9a-zA-Z]+$/, 'Ingresa un número de pasaporte válido')
        .required('Ingresa el número de tu pasaporte'),
      otherwise: yup
        .string()
        .trim()
        .matches(/^[0-9]+$/, 'Ingresa un número de identificación válido')
        .max(10, 'Máximo 10 dígitos')
        .required('Ingresa tu número de identificación')
    }),
    department: yup
      .string()
      .notOneOf([DEFAULT_DEPARTMENT], 'Selecciona un departamanto')
      .required('Selecciona un departamanto'),
    locality: yup
      .string()
      .notOneOf([DEFAULT_LOCALITY], 'Selecciona un municipio')
      .required('Selecciona un municipio'),
    cellPhoneNumber: yup
      .string()
      .trim()
      .matches(/^[1-9]\d{9}$/, 'Ingresa un número celular válido')
      .max(10, 'Máximo 10 dígitos')
      .required('Ingresa un número celular'),
    email: yup
      .string()
      .email('Ingresa un correo electrónico valido')
      .test(
        'is-ascii',
        'Este campo no puede contener tildes ni caracteres especiales',
        isPrintableAscii
      )
      .lowercase()
      .trim()
      .required('Ingresa un correo electrónico'),
    description: yup
      .string()
      .trim()
      .required('Ingresa un motivo de contacto'),
    terms: yup
      .bool()
      .oneOf([true], 'Debes aceptar los terminos y condiciones')
      .required('Debes aceptar los terminos y condiciones'),
    habeasData: yup
      .bool()
      .oneOf(
        [true],
        'Debes aceptar la política de tratamiento de datos personales'
      )
      .required('Debes aceptar la política de tratamiento de datos personales')
  })
  .required();

const DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  identification: '',
  identificationType: IDENTIFICATION_TYPE.CC,
  department: DEFAULT_DEPARTMENT,
  locality: DEFAULT_LOCALITY,
  cellPhoneNumber: '',
  email: '',
  description: '',
  terms: false,
  habeasData: false
};

const ContactMeDialog = ({ open = true, onClose, contractId }) => {
  const currentUser = useContext(UserContext);
  const authToken = _get(currentUser, 'token');
  const setAlert = useContext(AlertsDispatchContext);
  const classes = useStyles();
  const smallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const {
    register,
    control,
    handleSubmit,
    errors,
    watch,
    setValue,
    formState: { isValid: isValidForm },
    trigger
  } = useForm({
    resolver: yupResolver(contactInfoSchema),
    defaultValues: currentUser
      ? {
          ...DEFAULT_VALUES,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          identificationType: currentUser.identificationType,
          identification: currentUser.identification,
          cellPhoneNumber: currentUser.phone,
          email: currentUser.email
        }
      : DEFAULT_VALUES,
    mode: 'onChange'
  });

  const formDepartment = watch('department');
  const formIdentificationType = watch('identificationType');

  const handleDepartmentChange = () => {
    setValue('locality', DEFAULT_LOCALITY);
  };

  const onIdentificationTypeChange = () => {
    trigger(['firstName', 'lastName']);
  };

  const onSubmit = useCallback(
    async data => {
      const {
        firstName,
        lastName,
        identification,
        identificationType,
        department,
        locality,
        cellPhoneNumber,
        email,
        description
      } = data;

      const response = await CreateConstructorContactAPI(authToken, {
        contractId,
        firstName,
        lastName:
          identificationType === IDENTIFICATION_TYPE.NIT ? '' : lastName,
        identification,
        identificationType,
        department,
        locality,
        cellPhoneNumber,
        email,
        description
      });

      if (!response.success) {
        const errorMessage = extractErrorMessage(response).message;
        setAlert({
          type: ALERT_TYPE.ERROR,
          message: errorMessage || 'Ocurrio un error, intenta de nuevo.'
        });
        return;
      }

      setAlert({
        type: ALERT_TYPE.SUCCESS,
        message: 'Tu solicitud ha sido enviada exitosamente.'
      });
      onClose();
    },
    [authToken, contractId, setAlert, onClose]
  );

  const renderActionButtons = () => (
    <Grid container justifyContent="space-between">
      <Grid item>
        <BaseButton variant="outlined" onClick={onClose}>
          Cancelar
        </BaseButton>
      </Grid>
      <Grid item>
        <BaseButton onClick={handleSubmit(onSubmit)} disabled={!isValidForm}>
          Enviar
        </BaseButton>
      </Grid>
    </Grid>
  );

  const renderContent = () => (
    <Grid container>
      <Grid item xs={12}>
        <Typography paragraph>
          Ingresa tus datos de contacto para asignarle un asesor:
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={formIdentificationType === IDENTIFICATION_TYPE.NIT ? 12 : 6}
          >
            <TextInput
              label={
                formIdentificationType === IDENTIFICATION_TYPE.NIT
                  ? 'Razón social'
                  : 'Nombres'
              }
              name="firstName"
              inputRef={register}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName && errors.firstName.message}
              fullWidth
            />
          </Grid>
          {formIdentificationType !== IDENTIFICATION_TYPE.NIT && (
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Apellidos"
                name="lastName"
                inputRef={register}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName && errors.lastName.message}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <CompositeSelectInput
              label="Identificación"
              options={IDENTIFICATION_TYPE_LABELS.Short}
              TextInputProps={{
                label: 'Identificación',
                name: 'identification',
                inputRef: register,
                fullWidth: true
              }}
              SelectInputProps={{
                name: 'identificationType',
                onChange: onIdentificationTypeChange,
                control
              }}
              error={Boolean(errors.identification)}
              helperText={
                errors.identification && errors.identification.message
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectInput
              label="Departamento"
              name="department"
              control={control}
              options={DEPARTMENT_OPTIONS}
              error={Boolean(errors.department)}
              helperText={errors.department && errors.department.message}
              onChange={handleDepartmentChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectInput
              label="Municipio"
              name="locality"
              control={control}
              options={LOCALITY_OPTIONS[formDepartment]}
              error={Boolean(errors.locality)}
              helperText={errors.locality && errors.locality.message}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Correo electrónico"
              name="email"
              inputRef={register}
              error={Boolean(errors.email)}
              helperText={errors.email && errors.email.message}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Celular"
              name="cellPhoneNumber"
              inputRef={register}
              error={Boolean(errors.cellPhoneNumber)}
              helperText={
                errors.cellPhoneNumber && errors.cellPhoneNumber.message
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextAreaInput
              label="Motivo de contacto"
              name="description"
              inputRef={register}
              margin="normal"
              maxRows={10}
              maxLength={2000}
              helperText={errors.description && errors.description.message}
              error={Boolean(errors.description)}
            />
          </Grid>
          <Grid item xs={12} className={classes.termsContainer}>
            <FormControlLabel
              control={
                <ControlledCheckboxInput
                  id="Pqr_input_terms"
                  name="terms"
                  control={control}
                />
              }
              label={
                <Typography className={classes.termsText}>
                  He leído y acepto los{' '}
                  <a
                    className={classes.link}
                    href={`${ROUTE_NAMES.information}/${
                      TERMS_ROUTES[TERMS.introduction]
                    }`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    términos y condiciones.
                  </a>
                </Typography>
              }
            />
            <ErrorMessage
              errors={errors}
              name="terms"
              message={errors.terms}
              as={<Typography color="error" />}
            />
          </Grid>
          <Grid item xs={12} className={classes.habeasDataContainer}>
            <FormControlLabel
              control={
                <ControlledCheckboxInput
                  id="Pqr_input_terms"
                  name="habeasData"
                  control={control}
                />
              }
              label={
                <Typography className={classes.termsText}>
                  He leído, acepto y autorizo el{' '}
                  <a
                    className={classes.link}
                    href={PrivacyPoliciesLink}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    tratamiento de mis datos personales.
                  </a>
                </Typography>
              }
            />
            <ErrorMessage
              errors={errors}
              name="habeasData"
              message={errors.habeasData}
              as={<Typography color="error" />}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <BaseDialog
      handleClose={onClose}
      open={open}
      title="Quiero que me contacten"
      actions={renderActionButtons}
      content={renderContent}
      contentSize="medium"
      actionsStyle={classes.baseDialogActions}
      fullScreen={smallScreen}
    />
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 18,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2)
  },

  baseDialogActions: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      flexDirection: 'column',
      padding: theme.spacing(2)
    },
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4)
  }
}));

export default ContactMeDialog;
